import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Deal, getHighCommissionLink, STORE_MAP } from '../utils/constants';
import { ArrowLeft, ExternalLink, ShieldCheck, Zap, Hammer } from 'lucide-react';

export default function DealPage() {
  const { id } = useParams();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeal = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'deals', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setDeal({ id: docSnap.id, ...docSnap.data() } as Deal); 
        } else {
          setDeal(null);
        }
      } catch (error) {
        console.error("Error fetching deal data:", error);
        setDeal(null);
      } finally {
        setLoading(false); 
      }
    };
    fetchDeal();
  }, [id]);

  const discount = (deal?.originalPrice && deal?.price && deal.originalPrice > deal.price) 
    ? Math.round(((deal.originalPrice - deal.price) / deal.originalPrice) * 100) 
    : 0;

  const storeDisplayName = deal?.store 
    ? (Object.keys(STORE_MAP).find(key => STORE_MAP[key] === deal.store) || deal.store)
    : 'Retailer';

  const descriptionSource = deal?.description || '';

  let aiAnswerCapsule = "";
  if (deal) {
    if (discount > 0) {
      aiAnswerCapsule = `The ${deal.title} by ${deal.brand || 'premium brand'} is currently listed at $${deal.price.toFixed(2)} at ${storeDisplayName}, which is a ${discount}% discount off the standard retail price of $${deal.originalPrice?.toFixed(2)}.`;
    } else {
      aiAnswerCapsule = `The ${deal.title} is currently available at $${deal.price.toFixed(2)} at ${storeDisplayName}.`;
    }
  }

  // 🌐 GENERATE NATIVE SCHEMA.ORG JSON-LD FOR AI
  const schemaOrgJSON = deal ? {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": deal.title,
    "image": deal.image ? [deal.image] : [],
    "description": descriptionSource || `${deal.brand || 'Unknown'} ${deal.category || 'EV Upgrades'} Deal`,
    "sku": deal.id,
    "brand": {
      "@type": "Brand",
      "name": deal.brand || "Unknown"
    },
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "priceCurrency": "USD",
      "price": deal.price,
      "priceValidUntil": new Date(Date.now() + 86400000 * 7).toISOString().split('T')[0],
      "itemCondition": deal.title?.toLowerCase().includes("refurb") 
  ? "https://schema.org/RefurbishedCondition" 
  : "https://schema.org/NewCondition",
      "availability": deal.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": storeDisplayName
      }
    },
    "additionalProperty": [] as any[]
  } : null;

  useEffect(() => {
    if (deal) {
      const discountText = discount > 0 ? `(${discount}% OFF) ` : "";
      const pageTitle = `${discountText}${deal.title} | Tool Deals Daily`;
      document.title = pageTitle;

      const metaDescription = deal.curatorNote 
        ? `${deal.curatorNote} - Get the ${deal.title} for just $${deal.price.toFixed(2)} at ${storeDisplayName}.`
        : `Save big on the ${deal.title}. Currently just $${deal.price.toFixed(2)} at ${storeDisplayName}. Limited time tool deal!`;

      let metaDescTag = document.querySelector('meta[name="description"]');
      if (!metaDescTag) {
        metaDescTag = document.createElement('meta');
        metaDescTag.setAttribute('name', 'description');
        document.head.appendChild(metaDescTag);
      }
      metaDescTag.setAttribute('content', metaDescription);

      const updateOGTag = (property: string, content: string) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateOGTag('og:title', pageTitle);
      updateOGTag('og:description', metaDescription);
      if (deal.image) updateOGTag('og:image', deal.image);
      updateOGTag('og:url', window.location.href);
    }
  }, [deal, discount, storeDisplayName]);

  if (loading) return (
    <div className="max-w-5xl mx-auto bg-white p-6 md:p-10 rounded-2xl shadow-xl mt-4 border border-gray-100 animate-pulse">
      <div className="w-40 h-5 bg-slate-200 rounded mb-8"></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="bg-slate-100 rounded-2xl h-[400px] w-full border-2 border-gray-50"></div>
        <div className="flex flex-col justify-center space-y-6">
          <div className="h-10 bg-slate-200 rounded w-3/4"></div>
          <div className="h-6 bg-slate-200 rounded w-1/2"></div>
          <div className="h-24 bg-blue-50 rounded w-full"></div>
          <div className="h-16 bg-slate-200 rounded w-1/3 mt-4"></div>
        </div>
      </div>
    </div>
  );

  if (!deal) return <div className="text-center py-20 text-slate-500 font-bold">Deal not found. It may have expired.</div>;

  return (
    <article className="max-w-5xl mx-auto bg-white p-6 md:p-10 rounded-2xl shadow-xl mt-4 border border-gray-100 animate-slideUp">
      
      {schemaOrgJSON && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrgJSON) }} />
      )}

      <Link to="/" className="inline-flex items-center text-slate-400 hover:text-blue-600 font-bold mb-8 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Live Feed
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <figure className="flex justify-center items-center bg-white rounded-2xl p-8 border-2 border-gray-50 min-h-[400px] m-0">
          <img 
            src={deal.image || undefined} 
            alt={deal.title || 'Tool Deal'} 
            className="max-w-full h-auto max-h-[400px] object-contain" 
          />
        </figure>

        <section className="flex flex-col justify-center">
          
          <header className="mb-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {deal.brand && (
                <span className="bg-slate-900 text-white text-xs font-black px-3 py-1.5 rounded-md uppercase tracking-wider shadow-sm">
                  {deal.brand}
                </span>
              )}
              {deal.manualLock && (
                <span className="flex items-center text-blue-700 text-xs font-bold bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-md shadow-sm" title="Data verified and locked by ToolDeals expert curators.">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> AI Verified Source
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-4xl font-black text-slate-900 leading-tight m-0">
              {deal.title}
            </h1>
          </header>

          {aiAnswerCapsule && (
            <p className="text-lg text-slate-700 font-medium mb-8 leading-relaxed border-l-4 border-blue-500 pl-4 bg-blue-50 py-3 pr-3 rounded-r-md">
              {aiAnswerCapsule}
            </p>
          )}

          {descriptionSource && (
            <p className="text-slate-600 mb-8 leading-relaxed italic border-b border-gray-50 pb-6">
              "{descriptionSource}"
            </p>
          )}

          <div className="flex items-end gap-4 mb-8 pb-8 border-b border-gray-100">
            <span className="text-5xl font-black text-red-600">${deal.price?.toFixed(2)}</span>
            {deal.originalPrice > deal.price && (
              <div className="flex flex-col">
                <span className="text-lg text-gray-400 line-through font-bold">${deal.originalPrice?.toFixed(2)}</span>
                <span className="text-sm font-black text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-200 mt-1">
                  {discount}% OFF
                </span>
              </div>
            )}
          </div>

          <div className="space-y-4 mb-10">
            {deal.curatorNote && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-5 rounded-r-xl shadow-sm">
                <p className="text-sm font-black text-yellow-800 flex items-center gap-2 mb-2 uppercase tracking-wider m-0">
                  <Zap className="w-4 h-4" /> Expert Insight
                </p>
                <p className="text-sm text-yellow-900 leading-relaxed font-medium mt-2">{deal.curatorNote}</p>
              </div>
            )}

            <dl className="grid grid-cols-2 gap-4 text-sm mt-6 m-0 p-0">
              {deal.store && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <dt className="text-gray-400 block text-[10px] font-black mb-1 uppercase tracking-wider">Retailer</dt>
                  <dd className="font-bold text-slate-800 m-0">{storeDisplayName}</dd>
                </div>
              )}
            </dl>

          </div>

          <a 
            href={deal.url ? getHighCommissionLink(deal.url) : '#'} 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 text-xl font-black py-5 rounded-xl shadow-lg transition-transform hover:scale-[1.02] flex items-center justify-center gap-2 border-b-4 border-yellow-600 active:border-b-0 active:translate-y-1"
          >
            Claim This Deal <ExternalLink className="w-6 h-6" />
          </a>
          <p className="text-center text-xs text-slate-400 font-bold mt-4 m-0">
            Prices are accurate at time of posting and subject to change.
          </p>

        </section>
      </div>
    </article>
  );
}